// Per-job game viewer. Each game row is expanded by default showing its
// life-total chart; the raw Forge log is hidden behind a "Show raw log"
// button per row so the page stays scannable until you actually want the
// text dump. Logs are fetched when the row renders (immediate, since rows
// start expanded) and cached by React Query.

import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { AlertCircle, Loader2, Sparkles } from "lucide-react";
import {
  getPlaytest,
  getPlaytestGames,
  getPlaytestGameLog,
  summarizePlaytest,
  type PlaytestGameSummary,
  type PlaytestJob as PlaytestJobType,
  type PlaytestSummary,
} from "../lib/api";
import { parseLifeEvents } from "../../../src/forge/parse-life-events.ts";
import LifeChart from "../components/LifeChart";
import MatchResults from "../components/MatchResults";
import MetricRail, { type Metric } from "../components/MetricRail";
import StatusDot, { type Status } from "../components/StatusDot";
import ProvenanceLine from "../components/ProvenanceLine";
import SummaryView from "../components/SummaryView";
import { estimateBedrockCost } from "../lib/bedrock-cost";
import { ACTION_COSTS } from "../lib/credits";
import { useRefreshBalance } from "../lib/useBalance";

function winnerText(w: PlaytestGameSummary["winner"]): string {
  if (w === "a") return "Deck A won";
  if (w === "b") return "Deck B won";
  if (w === "draw") return "Draw";
  if (w === "dnf") return "Did not finish";
  return "—";
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const k = n / 1024;
  if (k < 1024) return `${k.toFixed(1)} KB`;
  return `${(k / 1024).toFixed(1)} MB`;
}

function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function PlaytestJob() {
  const { id = "" } = useParams<{ id: string }>();
  const jobQuery = useQuery({
    queryKey: ["playtest-job", id],
    queryFn: () => getPlaytest(id),
    enabled: !!id,
  });
  const gamesQuery = useQuery({
    queryKey: ["playtest-job-games", id],
    queryFn: () => getPlaytestGames(id),
    enabled: !!id,
  });

  if (!id) return null;

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-6">
      <nav className="text-[11px]">
        <Link to="/playtest/history" className="text-fg-subtle hover:text-fg">
          ← Back to history
        </Link>
      </nav>

      {jobQuery.isLoading && <div className="text-sm text-fg-muted">Loading…</div>}

      {jobQuery.error && (
        <div className="surface p-4 text-sm text-danger flex items-center gap-2">
          <AlertCircle size={14} strokeWidth={1.75} />
          {(jobQuery.error as Error).message}
        </div>
      )}

      {jobQuery.data && (
        <>
          <JobHero job={jobQuery.data} games={gamesQuery.data?.games} />

          <MatchResults
            job={jobQuery.data}
            elapsedMs={
              jobQuery.data.completed_at
                ? new Date(jobQuery.data.completed_at).getTime() -
                  new Date(jobQuery.data.started_at).getTime()
                : 0
            }
            showDetailsLink={false}
          />

          <SummarySection
            jobId={id}
            canSummarize={jobQuery.data.status === "complete"}
          />

          <section className="surface">
            <header className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase tracking-wider text-fg-subtle">
                Games
              </span>
              <span className="text-[11px] text-fg-subtle font-mono inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: "#60a5fa" }} />A
                <span className="inline-block w-2 h-2 rounded-sm ml-2" style={{ backgroundColor: "#f87171" }} />B
              </span>
            </header>
            {gamesQuery.isLoading && (
              <div className="p-5 text-sm text-fg-muted">Loading games…</div>
            )}
            {gamesQuery.data && gamesQuery.data.count === 0 && (
              <div className="p-5 text-sm text-fg-muted">
                No per-game logs captured. This job likely ran before per-game
                log persistence shipped — only the summary above is available.
              </div>
            )}
            {gamesQuery.data && gamesQuery.data.count > 0 && (
              <ul className="divide-y divide-border">
                {gamesQuery.data.games.map((g) => (
                  <GameRow key={g.game_index} jobId={id} summary={g} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// Hero block for the job page. Display-serif deck matchup title + a
// single-accent MetricRail of real summary numbers (W-L-D, games, avg
// duration, elapsed). Provenance line at the bottom ties it to the job
// record. This is the "stat specimen" home promised during the landing
// research — only real fields are rendered, no fabricated turn counts.
function JobHero({
  job,
  games,
}: {
  job: PlaytestJobType;
  games?: PlaytestGameSummary[];
}) {
  const live = job.outcome;
  const completed = live.games_played + live.dnf;

  // Status mapping from job.status → our StatusDot tones.
  const statusTone: Status =
    job.status === "complete" ? "success"
    : job.status === "error"  ? "error"
    : "active";

  const elapsedMs = job.completed_at
    ? new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()
    : 0;

  // Average per-game duration computed from the per-game summary payload
  // when it's loaded. We deliberately do NOT fabricate a turn count —
  // duration_ms is the honest "how long did each match take" proxy.
  const avgMs: number | null = games && games.length > 0
    ? games.reduce((acc, g) => acc + (g.duration_ms ?? 0), 0) / games.length
    : null;

  const metrics: Metric[] = [
    { label: "Wins A", value: live.deck_a_wins, tone: "accent" },
    { label: "Wins B", value: live.deck_b_wins },
    { label: "Draws", value: live.draws },
    { label: "Games", value: `${completed}/${job.games}` },
    { label: "Avg match", value: avgMs != null ? `${(avgMs / 1000).toFixed(1)}s` : "—" },
  ];

  return (
    <section className="space-y-5">
      <header className="space-y-3">
        <div className="eyebrow">Playtest</div>
        <h1 className="font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
          {job.deck_a_theme}
          <span className="text-fg-subtle"> vs </span>
          <span className="foil-subtle">{job.deck_b_theme}</span>
        </h1>
        <StatusDot status={statusTone} label={job.status.toUpperCase()} />
      </header>

      <MetricRail metrics={metrics} size="lg" />

      <ProvenanceLine
        fields={[
          { label: "job", value: job.id.slice(0, 12) + "…" },
          { label: "started", value: new Date(job.started_at).toLocaleString() },
          ...(job.completed_at
            ? [{ label: "wall", value: `${(elapsedMs / 1000).toFixed(0)}s` }]
            : []),
          ...(job.forge_version
            ? [{ label: "forge", value: job.forge_version }]
            : []),
        ]}
      />
    </section>
  );
}

function GameRow({
  jobId,
  summary,
}: {
  jobId: string;
  summary: PlaytestGameSummary;
}) {
  const [showLog, setShowLog] = useState(false);
  const logQuery = useQuery({
    queryKey: ["playtest-game-log", jobId, summary.game_index],
    queryFn: () => getPlaytestGameLog(jobId, summary.game_index),
  });

  const lifePoints = logQuery.data
    ? parseLifeEvents(logQuery.data.log)
    : [];

  return (
    <li>
      <div className="flex items-center gap-3 px-5 py-3">
        <span className="font-mono tabular-nums text-xs text-fg-subtle w-12">
          #{summary.game_index + 1}
        </span>
        <span className="text-sm flex-1">{winnerText(summary.winner)}</span>
        <span className="font-mono tabular-nums text-xs text-fg-subtle">
          {fmtMs(summary.duration_ms)}
        </span>
        <span className="font-mono tabular-nums text-xs text-fg-subtle">
          {fmtBytes(summary.log_bytes)}
        </span>
      </div>

      <div className="px-5 pb-4 pt-1 bg-bg-raised/10 border-t border-border space-y-3">
        {logQuery.isLoading && (
          <div className="text-xs text-fg-muted">Loading log…</div>
        )}
        {logQuery.error && (
          <div className="text-xs text-danger">
            {(logQuery.error as Error).message}
          </div>
        )}
        {logQuery.data && lifePoints.length >= 2 && (
          <LifeChart
            games={[{
              game_index: summary.game_index,
              winner: summary.winner,
              points: lifePoints,
            }]}
          />
        )}
        {logQuery.data && (
          <div>
            <button
              type="button"
              onClick={() => setShowLog((v) => !v)}
              className="text-[11px] px-2.5 py-1 rounded border border-border bg-bg-raised hover:bg-bg-raised/70 text-fg-muted"
            >
              {showLog ? "Hide raw log" : "Show raw log"}
            </button>
            {showLog && (
              <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-fg-muted max-h-[60vh] overflow-y-auto">
                {logQuery.data.log}
              </pre>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

// LLM-generated match recap. Button triggers generation; per-game life
// charts live in the GameRow components below, not here.
function SummarySection({
  jobId,
  canSummarize,
}: {
  jobId: string;
  canSummarize: boolean;
}) {
  const [data, setData] = useState<PlaytestSummary | null>(null);
  const refreshBalance = useRefreshBalance();
  const mutation = useMutation({
    mutationFn: () => summarizePlaytest(jobId),
    onSuccess: (res) => {
      setData(res);
      // Server charged match_summary credits unless the result was cached.
      if (!res.cached) refreshBalance();
    },
  });

  return (
    <section className="surface">
      <header className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-wider text-fg-subtle flex items-center gap-1.5">
          <Sparkles size={12} strokeWidth={1.75} /> Summary
        </span>
        {!data && (
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSummarize || mutation.isPending}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border bg-bg-raised hover:bg-bg-raised/70 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? (
              <>
                <Loader2 size={12} strokeWidth={1.75} className="animate-spin" />
                <span>
                  Summarizing<AnimatedDots />
                </span>
              </>
            ) : (
              <>
                <span>Summarize match</span>
                <span className="text-[10px] font-mono opacity-70">· {ACTION_COSTS.match_summary}</span>
              </>
            )}
          </button>
        )}
        {data && (() => {
          const cost = estimateBedrockCost(data.model, data.tokens_in, data.tokens_out);
          const promptTitle = cost != null
            ? `Summary generated under prompt ${data.prompt_version} · est. Bedrock cost $${cost.toFixed(3)} (${data.tokens_in} in / ${data.tokens_out} out)`
            : `Summary generated under prompt ${data.prompt_version}`;
          return (
            <div className="flex flex-wrap gap-2 text-[11px] text-fg-subtle font-mono tabular-nums">
              <span>{data.model}</span>
              <span>·</span>
              <span title={promptTitle}>
                prompt {data.prompt_version}
                {cost != null && (
                  <span className="text-fg-subtle/70">
                    {" "}(${cost.toFixed(2)})
                  </span>
                )}
              </span>
              {data.cached && (
                <>
                  <span>·</span>
                  <span>cached</span>
                </>
              )}
            </div>
          );
        })()}
      </header>
      {!canSummarize && (
        <div className="px-5 py-4 text-xs text-fg-muted">
          Only completed matches can be summarized.
        </div>
      )}
      {canSummarize && !data && !mutation.isPending && !mutation.error && (
        <div className="px-5 py-4 text-xs text-fg-muted">
          Generate an LLM recap covering how the match was won, each deck's
          strengths and weaknesses, and mana-curve performance.
        </div>
      )}
      {mutation.isPending && <SummarySkeleton />}
      {mutation.error && (
        <div className="px-5 py-4 text-xs text-danger flex items-center gap-2">
          <AlertCircle size={12} strokeWidth={1.75} />
          {(mutation.error as Error).message}
        </div>
      )}
      {data && <SummaryView summary={data.summary} />}
    </section>
  );
}

// Animated "..." that cycles through 0-3 dots on a fixed cadence. Pure
// CSS doesn't handle variable-length text cycling cleanly, so we drive
// it from state. The 400ms tick balances "clearly animating" against
// "not distracting during a 15s wait."
function AnimatedDots() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((v) => (v + 1) % 4), 400);
    return () => clearInterval(id);
  }, []);
  return <span className="inline-block w-[1.5em] text-left">{".".repeat(n)}</span>;
}

// Shimmer skeleton shown during the ~15s Bedrock round-trip. Four
// paragraph-shaped bars mirror the four-section summary layout so the
// user gets a preview of what's coming. animate-pulse is tailwind built-in.
function SummarySkeleton() {
  return (
    <div className="px-5 py-4 space-y-4 animate-pulse">
      {[...Array(4)].map((_, sectionIdx) => (
        <div key={sectionIdx} className="space-y-2">
          <div className="h-3 w-36 bg-bg-raised rounded" />
          <div className="h-2 w-full bg-bg-raised/70 rounded" />
          <div className="h-2 w-[92%] bg-bg-raised/70 rounded" />
          <div className="h-2 w-[78%] bg-bg-raised/70 rounded" />
        </div>
      ))}
    </div>
  );
}
